import Axios, { type AxiosError, type AxiosRequestConfig } from "axios";

import { ENV } from "@/features/config";

import { setupInterceptors } from "./interceptors";

export interface NmibleAxiosRequestConfig extends AxiosRequestConfig {
  /** By default on 401 or 403 response interceptor will automatically log user out, if true that behviour won't happen */
  skipAuthErrorLogout?: boolean;
  /** If true, the global mutation/query error toast won't fire for this request — the caller handles errors inline. */
  skipGlobalErrorToast?: boolean;
  /**
   * Backend error codes (e.g. "PMT-05") this request renders in place, on the
   * step that failed. The global toast stays silent for those codes on this
   * request only; every other code, and the same code from any other request,
   * still toasts.
   */
  inlineErrorCodes?: string[];
  /** Internal: set by the response interceptor after a token-refresh retry to prevent loops. */
  _retried?: boolean;
}

const encodeQueryParam = (value: string) =>
  encodeURIComponent(value)
    .replace(/%3A/gi, ":")
    .replace(/%24/g, "$")
    .replace(/%2C/gi, ",")
    .replace(/%20/g, "+")
    .replace(/%5B/gi, "[")
    .replace(/%5D/gi, "]");

/**
 * Deployed apps serve the v3 API from the same origin under `/api`; the dev
 * server, vitest and the e2e build still call the API host from the env file
 * directly.
 */
export const API_V3_BASE_URL =
  import.meta.env.DEV || import.meta.env.MODE === "e2e"
    ? ENV.NMIBLE.SERVER_V3
    : "/api";

export const nmibleV3API = Axios.create({
  baseURL: API_V3_BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  paramsSerializer: {
    encode: encodeQueryParam,
    indexes: null,
  },
});

export const loqateAPI = Axios.create({
  baseURL: ENV.LOQATE.SERVER,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

setupInterceptors(nmibleV3API);

export const nmibleInstanceV3 = <T>(
  config: NmibleAxiosRequestConfig,
  options?: NmibleAxiosRequestConfig
) => {
  const source = Axios.CancelToken.source();
  const promise = nmibleV3API<T>({
    ...config,
    ...options,
    cancelToken: source.token,
  }).then(({ data }) => data);

  return Object.assign(promise, {
    cancel: () => {
      source.cancel("Query was cancelled");
    },
  });
};

export type ErrorType<Error> = AxiosError<Error>;
export type BodyType<BodyData> = BodyData;

export default nmibleInstanceV3;
